<template>
    <Dialog
        v-model:visible="modalOpen"
        appendTo="body"
        dismissableMask
        :draggable="false"
        header=" "
        modal
        :style="{ width: '500px', 'max-width': '672px' }"
        @hide="cleanupCancelUpdate"
    >
        <div class="flex flex-col p-6">
            <slot name="header">
                <template v-if="context === 'employee'">
                    <h3 class="text-center text-xl font-semibold">Closinglock Review</h3>
                    <div class="mb-9 text-center text-base font-normal text-ds-primary-400">
                        Decide if Closinglock will cover transactions that use this ID verification.
                    </div>
                </template>
                <template v-else>
                    <h3 class="text-center text-xl font-semibold">Are you sure?</h3>
                    <div class="text-basefont-normal mb-9 text-center text-ds-primary-400">
                        By overriding the failed result, you have determined this client is not a risk to continue with the
                        transaction.
                    </div>
                </template>
            </slot>
            <div
                v-if="props.context === 'employee'"
                class="my-2"
            >
                <h4 class="text-base text-content-secondary">Employee:</h4>
                <input
                    class="w-full rounded-md border-ds-primary-300 bg-ds-primary-100 px-3 py-2"
                    readonly
                    type="text"
                    :value="currentUserService.currentUser ? currentUserService.currentUser.name : ''"
                />
            </div>
            <div
                v-if="props.context === 'employee'"
                class="my-2"
            >
                <h4 class="text-base text-content-secondary">Result:</h4>
                <div class="flex">
                    <div
                        v-for="(option, idx) in radioOptions"
                        :key="idx"
                        class="mb-4 mr-4 items-center"
                    >
                        <input
                            v-model="selectedOption"
                            :id="option.id"
                            class="mr-2 h-4 w-4 border-ds-primary-300 text-ds-primary-900 focus:ring-ds-primary-900"
                            :name="option.value"
                            type="radio"
                            :value="option.value"
                        />
                        <label :for="option.id">{{ option.label }}</label>
                    </div>
                </div>
            </div>
            <div class="my-2">
                <h4 class="text-sm text-content-secondary after:ml-1 after:text-ds-red-500 after:content-['*']">Reasons why:</h4>
                <textarea
                    v-model="overrideReason"
                    class="mb-4 w-full rounded-md border-ds-primary-300 px-3 py-2"
                    placeholder="Please provide a reason for the override"
                    rows="4"
                ></textarea>
            </div>
            <div class="flex flex-col gap-2 font-medium">
                <DsButton
                    :disabled="!overrideReason"
                    size="lg"
                    variant="primary"
                    @click="submitNewResult"
                >
                    <slot name="success-button">{{ confirmActionText }}</slot>
                </DsButton>
                <DsButton
                    size="lg"
                    variant="secondary"
                    @click="cleanupCancelUpdate"
                >
                    <slot name="cancel-button">{{ cancelActionText }}</slot>
                </DsButton>
            </div>
        </div>
    </Dialog>
</template>

<script setup lang="ts">
import type { IDVReviewEntry } from '@/api/interfaces/identity-verification.api';
import { IDVStatus } from '@/api/interfaces/identity-verification.api';
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import { useCurrentUserService } from '@/services/current-user.service';
import Dialog from 'primevue/dialog';
import { onMounted, ref, watch } from 'vue';
const currentUserService = useCurrentUserService();

type IdvStatusAvailableOption = IDVStatus.amended | IDVStatus.confirmed_failed;

interface RadioOption {
    id: string;
    label: string;
    value: IdvStatusAvailableOption;
}

type IDVUpdateEmitPayload = {
    idv: IDVReviewEntry['id'];
    status: IDVStatus;
    reason: string;
};

const radioOptions: RadioOption[] = [
    {
        id: 'pass',
        label: 'Pass',
        value: IDVStatus.amended,
    },
    {
        id: 'fail',
        label: 'Fail',
        value: IDVStatus.confirmed_failed,
    },
];

const props = withDefaults(
    defineProps<{
        idvEntryToUpdate: IDVReviewEntry['id'] | null;
        context?: 'employee' | 'admin';
        confirmActionText?: string;
        cancelActionText?: string;
    }>(),
    {
        idvEntryToUpdate: null,
        context: 'employee',
        confirmActionText: 'Submit New Result',
        cancelActionText: 'Cancel',
    },
);

const modalOpen = ref<boolean>(!!props.idvEntryToUpdate);
const overrideReason = ref<string>('');

onMounted(() => {
    if (props.idvEntryToUpdate) {
        selectedOption.value = IDVStatus.amended;
    }
});

watch(
    () => props.idvEntryToUpdate,
    () => {
        modalOpen.value = !!props.idvEntryToUpdate;
    },
);

const emit = defineEmits<{
    (event: 'update-idv-status', payload: IDVUpdateEmitPayload): void;
    (event: 'cancel-idv-update'): void;
}>();

const selectedOption = ref<IdvStatusAvailableOption>(IDVStatus.amended);

const submitNewResult = () => {
    if (!props.idvEntryToUpdate) return;
    emit('update-idv-status', {
        idv: props.idvEntryToUpdate,
        status: selectedOption.value,
        reason: overrideReason.value,
    });
    modalOpen.value = false;
};

const cleanupCancelUpdate = () => {
    // close the modal
    modalOpen.value = false;
    // emit the cancel event to the parent
    overrideReason.value = '';
    selectedOption.value = IDVStatus.amended;
    emit('cancel-idv-update');
};
</script>

<style scoped></style>
